<script setup lang="ts">
import CompanyCreateDialog from '@/components/companies/CompanyCreateDialog.vue'
import FinestButton from '@/components/FinestButton.vue'
import { ESTATE_OBJECT_IMPORT_ACCEPT, useEstateObjectImport } from '@/composables/useEstateObjectImport'
import { useFeatureFlagStore } from '@/stores/features'
import { computed, ref, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  hasPendingImport: boolean
}>()

const { t } = useI18n()

const features = useFeatureFlagStore()

const portfolioImportEnabled = computed(() => features.enabled('portfolio_import'))
const companyImportEnabled = computed(() => features.enabled('handelsregister_ai'))
const visible = computed(() => portfolioImportEnabled.value || companyImportEnabled.value)

const { uploading, upload } = useEstateObjectImport()

const processing = computed(() => props.hasPendingImport || uploading.value)

const fileInput = useTemplateRef<HTMLInputElement>('fileInput')

const dropTargetActive = ref(false)

const companyDialogVisible = ref(false)

function submit(file: File | undefined): void {
  if (processing.value) {
    return
  }

  upload(file)
}

function onSelect(event: Event): void {
  const input = event.target as HTMLInputElement

  submit(input.files?.[0])
  input.value = ''
}

function onDrop(event: DragEvent): void {
  dropTargetActive.value = false
  submit(event.dataTransfer?.files?.[0])
}
</script>

<template>
  <section v-if="visible" class="fh-card @container p-6">
    <div class="flex items-center gap-2">
      <p class="section-label section-label--sm">{{ t('eyebrow') }}</p>
      <span class="bg-info-subtle text-action rounded-full px-2 py-0.5 text-xs font-bold">{{ t('beta') }}</span>
    </div>
    <h2 class="mt-1 text-xl font-bold tracking-tight">{{ t('title') }}</h2>
    <p class="text-ink-2 mt-3 max-w-[74ch]">{{ t('description') }}</p>

    <div class="mt-5 grid gap-4 @xl:grid-cols-2 @4xl:grid-cols-3">
      <div
        v-if="portfolioImportEnabled"
        class="flex flex-col gap-3 rounded-xl border p-4 transition-colors"
        :class="dropTargetActive ? 'border-action bg-info-subtle-hover' : 'border-line bg-surface'"
        @dragover.prevent="dropTargetActive = !processing"
        @dragleave.prevent="dropTargetActive = false"
        @drop.prevent="onDrop">
        <div class="flex items-center gap-2.5">
          <span class="bg-info-subtle text-action flex size-8 flex-none items-center justify-center rounded-lg">
            <i class="pi pi-sparkles text-sm!" aria-hidden="true" />
          </span>
          <p class="font-bold">{{ t('tools.portfolioImport.title') }}</p>
        </div>

        <p class="text-ink-2 text-sm">{{ t('tools.portfolioImport.description') }}</p>

        <div v-if="processing" class="text-ink-2 mt-auto flex items-start gap-2 pt-1 text-xs" role="status">
          <i class="pi pi-spinner text-action mt-0.5 animate-spin text-sm!" aria-hidden="true" />
          <span>{{ t('tools.portfolioImport.processing') }}</span>
        </div>

        <div v-else class="mt-auto pt-1">
          <FinestButton icon="pi pi-upload" :label="t('tools.portfolioImport.action')" @click="fileInput?.click()" />
          <p class="text-ink-3 mt-2 text-xs">{{ t('tools.portfolioImport.hint') }}</p>
        </div>

        <input ref="fileInput" type="file" class="hidden" :accept="ESTATE_OBJECT_IMPORT_ACCEPT" @change="onSelect" />
      </div>

      <div v-if="companyImportEnabled" class="border-line bg-surface flex flex-col gap-3 rounded-xl border p-4">
        <div class="flex items-center gap-2.5">
          <span
            class="bg-area-company-subtle text-area-company flex size-8 flex-none items-center justify-center rounded-lg">
            <i class="pi pi-building text-sm!" aria-hidden="true" />
          </span>
          <p class="font-bold">{{ t('tools.companyImport.title') }}</p>
        </div>

        <p class="text-ink-2 text-sm">{{ t('tools.companyImport.description') }}</p>

        <div class="mt-auto pt-1">
          <FinestButton
            icon="pi pi-plus"
            :label="t('tools.companyImport.action')"
            @click="companyDialogVisible = true" />
        </div>

        <CompanyCreateDialog v-model:visible="companyDialogVisible" :show-trigger="false" />
      </div>

      <div class="border-line bg-surface-2 flex flex-col gap-3 rounded-xl border border-dashed p-4">
        <div class="flex items-center gap-2.5">
          <span class="bg-surface-3 text-ink-3 flex size-8 flex-none items-center justify-center rounded-lg">
            <i class="pi pi-microchip-ai text-sm!" aria-hidden="true" />
          </span>
          <p class="text-ink-2 font-bold">{{ t('tools.more.title') }}</p>
        </div>

        <p class="text-ink-3 text-sm">{{ t('tools.more.description') }}</p>
      </div>
    </div>
  </section>
</template>

<i18n>
{
  "en": {
    "eyebrow": "AI tools",
    "beta": "Beta",
    "title": "Let AI do the typing",
    "description": "These tools are new and still in beta. They read your documents and prefill what we would otherwise ask you to enter by hand.",
    "tools": {
      "portfolioImport": {
        "title": "AI portfolio list import",
        "description": "Upload your property portfolio as an Excel or PDF file – any structure, any layout. Processing runs with AI in the background.",
        "action": "Upload portfolio list",
        "hint": "or drag a file here · XLSX, XLS, PDF",
        "processing": "Your portfolio list is being processed. This can take 10 to 15 minutes. You will receive an email when it is finished."
      },
      "companyImport": {
        "title": "AI company import",
        "description": "Enter the company name – AI pulls the commercial register data and creates the company with it.",
        "action": "Add company"
      },
      "more": {
        "title": "More tools to come",
        "description": "We are working on further AI tools. They will appear here as soon as they are ready."
      }
    }
  },
  "de": {
    "eyebrow": "KI-Tools",
    "beta": "Beta",
    "title": "Tippen übernimmt die KI",
    "description": "Diese Tools sind neu und noch in der Beta. Sie lesen deine Unterlagen aus und füllen vor, was du sonst von Hand einträgst.",
    "tools": {
      "portfolioImport": {
        "title": "KI-Bestandslisten-Import",
        "description": "Lade deinen Immobilienbestand als Excel- oder PDF-Datei hoch – beliebige Struktur und beliebiges Layout. Die Verarbeitung läuft mit KI im Hintergrund.",
        "action": "Bestandsliste hochladen",
        "hint": "oder Datei hierher ziehen · XLSX, XLS, PDF",
        "processing": "Deine Bestandsliste wird verarbeitet. Das kann 10 bis 15 Minuten dauern. Du erhältst nach Abschluss eine E-Mail."
      },
      "companyImport": {
        "title": "KI-Unternehmensimport",
        "description": "Gib den Namen des Unternehmens ein – die KI holt die Handelsregisterdaten und legt das Unternehmen damit an.",
        "action": "Unternehmen hinzufügen"
      },
      "more": {
        "title": "Weitere Tools folgen",
        "description": "Wir arbeiten an weiteren KI-Tools. Sobald sie fertig sind, findest du sie hier."
      }
    }
  }
}
</i18n>
